import React from "react";
import { Button } from "./ui/button";
import { Save, Pencil } from "lucide-react";

/**
 * Excel-style sticky bulk-edit components shared across admin pages.
 *
 * Pattern (proven on /admin/stock):
 *   1. Page renders a "Bulk edit" toggle button.
 *   2. When ON, the page renders cells as inline <input>/<select>/<switch>.
 *   3. A sticky emerald banner pins to the top — shows the count of dirty rows
 *      and exposes Reset + Save changes buttons.
 *   4. On save the page batches PUT requests, then refreshes and clears edits.
 *
 * `useBulkEdits` keeps the state machinery tiny and isomorphic across pages.
 */

export function useBulkEdits(originals, getId = (x) => x.id) {
    const [edits, setEdits] = React.useState({});

    const cellValue = React.useCallback((id, field, original) => {
        const patch = edits[id];
        if (patch && patch[field] !== undefined) return patch[field];
        return original;
    }, [edits]);

    const setCell = React.useCallback((id, field, value, original) => {
        setEdits((prev) => {
            const next = { ...prev };
            const patch = { ...(next[id] || {}) };
            // Numeric compare with tolerance
            const isNum = typeof original === "number";
            const same = isNum
                ? Math.abs(parseFloat(value || 0) - parseFloat(original || 0)) < 0.0001
                : value === (original ?? "");
            if (same) delete patch[field];
            else patch[field] = value;
            if (Object.keys(patch).length === 0) delete next[id];
            else next[id] = patch;
            return next;
        });
    }, []);

    const dirtyIds = React.useMemo(
        () => Object.keys(edits).filter((k) => Object.keys(edits[k] || {}).length > 0),
        [edits],
    );

    const dirtyPatches = React.useCallback(() => {
        return dirtyIds.map((id) => {
            const orig = originals.find((o) => getId(o) === id);
            return { id, original: orig, patch: edits[id] };
        });
    }, [dirtyIds, edits, originals, getId]);

    const reset = React.useCallback(() => setEdits({}), []);

    return { edits, setCell, cellValue, dirtyIds, dirtyPatches, reset };
}

export default function BulkEditBanner({
    dirtyCount,
    busy,
    onReset,
    onSave,
    helperText,
    testIdPrefix = "bulk",
}) {
    const hasEdits = dirtyCount > 0;
    return (
        <div
            className="sticky top-0 z-20 bg-emerald-700 text-white px-4 py-3 mb-4 flex items-center justify-between flex-wrap gap-2 shadow-md"
            data-testid={`${testIdPrefix}-banner`}
        >
            <div className="text-sm">
                <strong className="font-serif text-lg">Inline edit mode</strong>
                <span className="ml-3 text-emerald-100">
                    {!hasEdits
                        ? (helperText || "Type into any cell to edit. Use Reset to discard, Save to commit all changes in one click.")
                        : `${dirtyCount} row${dirtyCount === 1 ? "" : "s"} with pending edits`}
                </span>
            </div>
            <div className="flex gap-2">
                <Button
                    onClick={onReset}
                    disabled={!hasEdits}
                    variant="outline"
                    className="rounded-none border-white text-emerald-800 bg-white hover:bg-emerald-50 disabled:opacity-50"
                    data-testid={`${testIdPrefix}-reset`}
                >
                    Reset
                </Button>
                <Button
                    onClick={onSave}
                    disabled={busy || !hasEdits}
                    className="rounded-none bg-white text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                    data-testid={`${testIdPrefix}-save`}
                >
                    <Save className="w-4 h-4 mr-2" />
                    {busy ? "Saving…" : `Save ${dirtyCount || "all"} change${dirtyCount === 1 ? "" : "s"}`}
                </Button>
            </div>
        </div>
    );
}

/** Reusable "Bulk edit" toggle button — same styling as Stock Register. */
export function BulkEditToggle({ active, onClick, testId = "toggle-bulk-mode", labelOff = "Bulk edit", labelOn = "Exit bulk edit" }) {
    return (
        <Button
            onClick={onClick}
            variant={active ? "default" : "outline"}
            className={`rounded-none ${active ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "border-emerald-400 text-emerald-700 hover:bg-emerald-50"}`}
            data-testid={testId}
        >
            <Pencil className="w-4 h-4 mr-2" />{active ? labelOn : labelOff}
        </Button>
    );
}
